import { matchesCategory } from "../../lib/allocations";
import { allocationCategories, categoryUse, type Need } from "../../lib/coverage";
import { useEffect, useMemo, useState } from "react";
import { Tags } from "lucide-react";
import type { DashboardData } from "../../types";
import { plural } from "../../lib/finance";
import { api } from "../../lib/api";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import LoadingLabel from "../../components/LoadingLabel";
import { useCategoryEditor } from "./use-category-editor";
import CategoryGroup from "./CategoryGroup";
import SecondaryHeading from "../../app/SecondaryHeading";

export default function CategorySettings({
  data,
  ensure,
  onSaved,
  onDirtyChange,
}: {
  data: DashboardData;
  /** Loads the ledger back far enough for what is asked of it. */
  ensure: (need: Need) => Promise<DashboardData | undefined>;
  onSaved: (entryOptions: DashboardData["entryOptions"]) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const entryOptions = data.entryOptions || {
    categories: [], subcategories: [], subcategoriesByCategory: {},
    incomeCategories: [], incomeSubcategories: [], incomeSubcategoriesByCategory: {},
  };
  const expense = useCategoryEditor(
    entryOptions.categories,
    entryOptions.subcategoriesByCategory,
  );
  const income = useCategoryEditor(
    entryOptions.incomeCategories,
    entryOptions.incomeSubcategoriesByCategory,
  );
  const [mergeFrom, setMergeFrom] = useState("");
  const [mergeTo, setMergeTo] = useState("");
  const [mergeConfirm, setMergeConfirm] = useState<ConfirmState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const dirty = expense.dirty || income.dirty;
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  // Every category and subcategory row asks this on each render — every keystroke while editing —
  // so the use is looked up in a set indexed once per load rather than scanned once per row.
  const used = useMemo(() => categoryUse(data), [data]);
  const hasExpenseTransactionUse = (name: string, subcategory?: string) =>
    used.has(JSON.stringify(subcategory ? ["expense", name, subcategory] : ["expense", name]));
  const hasIncomeTransactionUse = (name: string, subcategory?: string) =>
    used.has(JSON.stringify(subcategory ? ["income", name, subcategory] : ["income", name]));
  async function save() {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const result = await api<{
        verified: boolean;
        entryOptions: DashboardData["entryOptions"];
      }>("settings/categories", {
        categories: expense.list,
        subcategoriesByCategory: expense.map,
        incomeCategories: income.list,
        incomeSubcategoriesByCategory: income.map,
        expectedOptions: entryOptions,
      });
      onSaved(result.entryOptions);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  // The merge names the exact entries it moves, so it needs every one of them, whatever its age.
  async function reviewMerge() {
    const everything = await ensure("all");
    const expected = (everything ?? data).transactions.filter((t) => matchesCategory(t, mergeFrom));
    const count = expected.length;
    setMergeConfirm({
      title: `Move ${plural(count, "transaction")}?`,
      message: `Move ${mergeFrom} to ${mergeTo}. Linked prices follow their transactions. Incompatible subcategories will be cleared. Any budget set on ${mergeFrom} will stop applying going forward (past months are unaffected); set a new one on ${mergeTo} afterward if needed. The old entry option can then be hidden separately.`,
      confirmLabel: "Merge categories",
      onConfirm: async () => { await api("settings/categories/merge", { from: mergeFrom, to: mergeTo, count, expected }); window.location.reload(); },
    });
  }

  const [activeTab, setActiveTab] = useState<"expense" | "income">("expense");

  return (
    <section className="panel settings-panel">
      <ConfirmDialog state={mergeConfirm} onClose={() => setMergeConfirm(null)} />

      <SecondaryHeading title="Categories" icon={<Tags size={18} />}>
        <button
          className="primary small"
          disabled={busy || !expense.list.length || !dirty}
          onClick={save}
        >
          {busy ? <LoadingLabel>Saving</LoadingLabel> : dirty ? "Save changes" : "Up to date"}
        </button>
      </SecondaryHeading>
      {error && <div className="inline-error">{error}</div>}
      {saved && <div className="inline-success">Categories saved.</div>}
      <p className="settings-note">
        Renaming or removing changes new-entry options only. Existing
        transaction categories are preserved.
      </p>

      <div className="pill-tabs category-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "expense"}
          className={`pill-tab ${activeTab === "expense" ? "active" : ""}`}
          onClick={() => setActiveTab("expense")}
        >
          Expense ({expense.list.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "income"}
          className={`pill-tab ${activeTab === "income" ? "active" : ""}`}
          onClick={() => setActiveTab("income")}
        >
          Income ({income.list.length})
        </button>
      </div>

      {activeTab === "expense" ? (
        <CategoryGroup editor={expense} hasTransactionUse={hasExpenseTransactionUse} />
      ) : (
        <CategoryGroup editor={income} hasTransactionUse={hasIncomeTransactionUse} />
      )}
      <small className="build-info">
        {data.version} · {data.buildCommit || "local build"}
      </small>
      <details className="category-maintenance"><summary>Advanced · Merge categories</summary>
        <p>Move historical transactions into an existing category. Subcategories that do not belong to the destination will be cleared. Save any category-option changes first.</p>
        <label>From<select value={mergeFrom} onChange={e => setMergeFrom(e.target.value)}><option value="">Choose category</option>{allocationCategories(data).map(c => <option key={c}>{c}</option>)}</select></label>
        <label>Into<select value={mergeTo} onChange={e => setMergeTo(e.target.value)}><option value="">Choose destination</option>{entryOptions.categories.filter(c => c !== mergeFrom).map(c => <option key={c}>{c}</option>)}</select></label>
        <button disabled={dirty || busy || !mergeFrom || !mergeTo} onClick={() => void reviewMerge()}>Review merge</button>
      </details>
    </section>
  );
}
